import React, { useState, useEffect } from 'react';
import { useIntl, getLocale } from 'umi';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import { IDomEditor, IEditorConfig, IToolbarConfig, i18nChangeLanguage } from '@wangeditor/editor';
import { Modal, Button } from 'tea-component';
import useCOS from '@home/hooks/useCOS';
import { v4 as uuidv4 } from 'uuid';
import '@wangeditor/editor/dist/css/style.css';
import { getPdfCos } from '@/pages/dashboard/services/dashboard';
import { ENV } from '@home/_config/config';

const EditModal = (props: any) => {
  const {
    closeModal,
    handelCancel: propsHandleCancel,
    isVisible,
    data: appendixContent,
    handleSubmit: propsHandelSubmit,
    title = '',
    setClickEditAppendix,
    setClickAddAppendix,
    clickAddAppendix,
    clickEditAppendix,
    saveLoading,
    ...resetProps } = props || {};

  const { formatMessage } = useIntl();
  const languageType = getLocale() === 'en-US' ? 'en' : 'zh-CN';
  i18nChangeLanguage(languageType);
  const { appendix: data, id } = appendixContent || {};

  // 受控的环境，预发布和正式
  const controlledEnv = ['pre', 'pro'];
  const isControlled = controlledEnv.includes(ENV);
  // 预发布&正式使用另一个桶
  const cosOptions = isControlled
    ? {
      customBucket: 'databrain-dashboard-1300342648',
      url: '/api/v1/dashboard/cos/auth',
    }
    : {};

  // 获取图片上传cos
  const { putObject } = useCOS(cosOptions);

  // editor 实例
  const [editor, setEditor] = useState<IDomEditor | null>(null); // TS 语法

  // 编辑器内容
  const [html, setHtml] = useState(data || '');
  const [isEmpty, setIsEmpty] = useState(true);
  // 编辑器内容
  const [appendixId, setAppendixId] = useState(id || null);

  // 工具栏配置
  const toolbarConfig: Partial<IToolbarConfig> = {
    toolbarKeys: [
      'bold',
      'underline',
      // 'through',
      'fontSize',
      'color',
      'bulletedList',
      'numberedList',
      {
        key: 'group-justify-style',
        title: 'justify',
        iconSvg: `<svg viewBox="0 0 1024 1024"><path d="M768 793.6v102.4H51.2v-102.4h716.8z m204.8-230.4v102.4H51.2v-102.4h921.6z m-204.8-230.4v102.4H51.2v-102.4h716.8zM972.8 102.4v102.4H51.2V102.4h921.6z"></path></svg>`,
        menuKeys: [
          'justifyLeft',
          'justifyRight',
          'justifyCenter',
          'justifyJustify',
        ],
      },
      'clearStyle',
      'undo',
      'redo',
    ],
    excludeKeys: [],
  };

  // 编辑器配置
  const editorConfig: Partial<IEditorConfig> = {
    placeholder: `${formatMessage({ id: 'dbh_studio_funcom_edit_modal_placeholder' })}...`,
    MENU_CONF: {
      fontSize: {
        fontSizeList: ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px'],
      },
      // 增加图片粘贴功能
      uploadImage: {
        async customUpload(file: File, insertFn: (url: string) => void) {
          const path = `/front/studio-report/${uuidv4()}.png`;
          const result = await putObject({ path, file });
          const params = { url: `https://${result.Location}` || "" };
          if (isControlled) {
            // 正式&预发cos桶url需要签名
            const data = await getPdfCos(params);
            insertFn(data);
          } else {
            // 直接使用上传结果的 URL
            insertFn(params.url);
          }
        }
      }  
    },
  };

  // 及时销毁 editor ，重要！
  useEffect(() => () => {
    if (editor) {
      editor.destroy();
      setEditor(null);
    }
  }, [editor]);

  useEffect(() => {
    if (clickAddAppendix) {
      setHtml("");
      setAppendixId(null);
    } 
    if(clickEditAppendix){
      setAppendixId(id);
      setHtml(data || '');
    }
  }, [data, clickAddAppendix, id]);

  const handleEditorChange = (editor: IDomEditor) => {
    const resultVal = editor.getHtml();
    const cleanedText = resultVal.replace(/<p>|<\/p>|<br>|&nbsp;|\s+/g, '').trim();
    const empty = editor.isEmpty() || cleanedText.length === 0; 
    setIsEmpty(empty);
    setHtml(resultVal);
  };

  // 提交
  const handleSubmit = () => {
    // 处理提交逻辑
    propsHandelSubmit?.(html, appendixId);
    setClickEditAppendix(false);
    setClickAddAppendix(false);
  };

  // 取消
  const handelCancel = () => {
    propsHandleCancel?.();
    setClickEditAppendix(false);
    setClickAddAppendix(false);
  };

  // 编辑器挂载
  const handleEditorCreated = (editorInstance: IDomEditor) => {
    setEditor(editorInstance);
  };

  return (
    <>
      <Modal
        visible={isVisible}
        size={965}
        caption={title || formatMessage({ id: 'Edit' })}
        onClose={() => {
          closeModal();
          setClickEditAppendix(false);
          setClickAddAppendix(false);
        }}
        {...(resetProps || {})}
      >
        <Modal.Body>
          <div style={{ border: '1px solid #ddd', zIndex: 100, position: 'relative' }}>
            <Toolbar
              editor={editor}
              defaultConfig={toolbarConfig}
              mode="simple"
              style={{ borderBottom: '1px solid #ccc' }}
            />

            <Editor
              defaultConfig={editorConfig}
              value={html}
              onCreated={handleEditorCreated}
              onChange={handleEditorChange}
              mode="simple"
              style={{ height: '500px', overflowY: 'hidden', width: '100%' }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            type="primary" 
            onClick={handleSubmit} 
            disabled={isEmpty}
            loading={saveLoading}
          >
            {formatMessage({ id: 'dbh_studio_funcom_edit_modal_comfirm' })}
          </Button>
          <Button
            type="weak"
            onClick={handelCancel}
          >
            {formatMessage({ id: 'dbh_studio_funcom_edit_modal_cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditModal;